import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import apiRequestHelper from "../utils/apiRequestHelper";

interface User {
  name: string;
  email: string;
  username: string;
  role: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  loginUser: (user: User, token: string) => void;
  logoutUser: (redirect?: boolean) => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const logoutUser = useCallback((redirect = true) => {
    localStorage.removeItem("token");
    localStorage.removeItem("authUser");
    setUser(null);
    setIsAuthenticated(false);
    if (redirect) window.location.href = "/login";
  }, []);

  // Validate token with backend on load
  const validateToken = useCallback(async () => {
    const token = localStorage.getItem("token");
    const userData = localStorage.getItem("authUser");

    if (!token || !userData) {
      setIsAuthenticated(false);
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      // NOTE: keeping behaviour as close as possible:
      // your interceptor already attaches Authorization, so we don't need to pass headers here.
      const response = await apiRequestHelper.get("/auth/validate-token");

      if (response.data?.valid) {
        setIsAuthenticated(true);
        setUser(response.data.user || JSON.parse(userData));
      } else {
        logoutUser(false); // clear without redirect
      }
    } catch (err) {
      logoutUser(false);
    } finally {
      setLoading(false);
    }
  }, [logoutUser]);

  useEffect(() => {
    validateToken();
  }, [validateToken]);

  const loginUser = useCallback((user: User, token: string) => {
    localStorage.setItem("authUser", JSON.stringify(user));
    localStorage.setItem("token", token);
    setUser(user);
    setIsAuthenticated(true);
  }, []);

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, user, loginUser, logoutUser, loading }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};
